import { axios } from "./axios";
import { env } from "../utils/env";
import { SplashScreenList } from "../types/SplashScreenList";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { ROUTE } from "../routes";
import { useNavigate } from "react-router-dom";

interface SplashScreenResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SplashScreenList[];
}

const divisionsParam = (divisions?: number[]) =>
  divisions?.length
    ? { divisions: [...divisions].sort((a, b) => a - b).join(",") }
    : {};

const fetchList = (authHeader: string, divisions?: number[]) => () =>
  axios.get<SplashScreenResponse>(
    `${env.verifyServiceUrl}individual/splash_screen/`,
    {
      headers: {
        Authorization: authHeader,
      },
      params: divisionsParam(divisions),
    }
  );

const fetchOne = (authHeader: string, id: number) => () =>
  axios.get<SplashScreenList>(
    `${env.verifyServiceUrl}individual/splash_screen/${id}/`,
    {
      headers: {
        Authorization: authHeader,
      },
    }
  );

/**
 * Every splash screen for the tenant, optionally filtered to Divisions
 * (`?divisions=1,2`).
 */
export const useSplashScreens = (divisions?: number[]) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  return useQuery(
    ["splashScreenList", divisionsParam(divisions)],
    fetchList(authHeader, divisions),
    {
      retry: false,
      keepPreviousData: true,
      select: (response) => response.data.results,
      onError: (err: any) => {
        const status = err?.response?.status ?? err?.request?.status;
        if (status === 500 || status === 503) {
          navigate(ROUTE.SERVER_ERROR);
        }
      },
    }
  );
};

/** One splash screen by id; disabled until an id is given (create mode). */
export const useSplashScreen = (id?: number) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  return useQuery(["splashScreen", id], fetchOne(authHeader, id), {
    enabled: typeof id === "number" && !Number.isNaN(id),
    retry: false,
    select: (response) => response.data,
    onError: (err: any) => {
      const status = err?.response?.status ?? err?.request?.status;
      if (status === 500 || status === 503) {
        navigate(ROUTE.SERVER_ERROR);
      }
    },
  });
};
